(() => {
  const state = { opener: null };
  const overlay = () => document.querySelector('[data-site-overlay]');

  function announce(message) {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 20);
  }

  function openDrawer(id, opener) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    closeDrawers(false);
    state.opener = opener || document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    const shade = overlay();
    if (shade) shade.hidden = false;
    if (opener) opener.setAttribute('aria-expanded', 'true');
    const focusTarget = drawer.querySelector('input:not([type="hidden"]), button, a');
    window.setTimeout(() => focusTarget?.focus(), 30);
  }

  function closeDrawers(restoreFocus = true) {
    document.querySelectorAll('[data-drawer].is-open').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
    });
    document.querySelectorAll('[data-drawer-open][aria-expanded="true"]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('drawer-open');
    const shade = overlay();
    if (shade) shade.hidden = true;
    if (restoreFocus && state.opener instanceof HTMLElement) state.opener.focus();
    state.opener = null;
  }

  function initCarousels(root = document) {
    root.querySelectorAll('[data-carousel]').forEach((carousel) => {
      if (carousel.dataset.ready === 'true') return;
      carousel.dataset.ready = 'true';
      const slides = [...carousel.querySelectorAll('[data-carousel-slide]')];
      const dots = [...carousel.querySelectorAll('[data-carousel-dot]')];
      const counters = [...carousel.querySelectorAll('[data-carousel-counter]')];
      let index = 0;

      function show(next) {
        if (!slides.length) return;
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== index; });
        dots.forEach((dot) => {
          if (Number(dot.dataset.carouselDot) === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        counters.forEach((counter) => { counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`; });
      }

      carousel.addEventListener('click', (event) => {
        const previous = event.target.closest('[data-carousel-prev]');
        const next = event.target.closest('[data-carousel-next]');
        const dot = event.target.closest('[data-carousel-dot]');
        if (previous) show(index - 1);
        if (next) show(index + 1);
        if (dot) show(Number(dot.dataset.carouselDot));
      });
      show(0);
    });
  }

  function selectedOptions(form) {
    return [...form.querySelectorAll('[data-option-position]')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((fieldset) => fieldset.querySelector('input:checked')?.value || null);
  }

  function initProductForms(root = document) {
    root.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.ready === 'true') return;
      form.dataset.ready = 'true';
      const json = form.querySelector('[data-variants-json]');
      if (!json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch { return; }
      const idInput = form.querySelector('input[name="id"]');
      const addButton = form.querySelector('[data-add-to-cart]');
      const price = form.closest('.product-info')?.querySelector('[data-main-product-price]');
      const availability = form.querySelector('[data-availability]');
      const requiredLineItem = form.querySelector('[data-required-line-item]');
      const productRoot = form.closest('[data-product-root]');
      const restockState = form.querySelector('[data-restock-state]');
      const soldOutOverlay = productRoot?.querySelector('[data-sold-out-overlay]');
      const mobileBar = productRoot?.querySelector('[data-mobile-purchase-bar]');
      const mobilePrice = mobileBar?.querySelector('[data-mobile-product-price]');
      const mobileAdd = mobileBar?.querySelector('[data-mobile-add]');

      function setInventoryState(isAvailable) {
        if (productRoot) productRoot.dataset.selectedVariantAvailable = String(isAvailable);
        if (restockState) {
          restockState.hidden = isAvailable;
          restockState.setAttribute('aria-hidden', String(isAvailable));
        }
        if (soldOutOverlay) {
          soldOutOverlay.hidden = isAvailable;
          soldOutOverlay.setAttribute('aria-hidden', String(isAvailable));
        }
        if (mobileBar) {
          mobileBar.dataset.available = String(isAvailable);
          if (!isAvailable) mobileBar.hidden = true;
        }
      }

      if (mobileBar && addButton && !mobileBar.dataset.ready) {
        mobileBar.dataset.ready = 'true';
        const observer = new IntersectionObserver((entries) => {
          const isAvailable = mobileBar.dataset.available === 'true';
          mobileBar.hidden = !isAvailable || entries[0].isIntersecting;
        }, { threshold: .1 });
        observer.observe(addButton);
      }

      function update() {
        const options = selectedOptions(form);
        const variant = variants.find((item) => item.options.every((value, i) => value === options[i]));
        const selectedLineItem = requiredLineItem?.querySelector('input:checked');
        form.querySelectorAll('[data-selected-option]').forEach((label) => {
          const position = Number(label.dataset.selectedOption);
          label.textContent = options[position - 1] || 'SELECT';
        });
        const lineItemLabel = form.querySelector('[data-selected-line-item]');
        if (lineItemLabel) lineItemLabel.textContent = selectedLineItem?.value || 'SELECT';
        if (!variant) {
          if (addButton) { addButton.disabled = true; addButton.textContent = 'CHOOSE OPTIONS'; }
          if (availability) availability.textContent = 'SELECT OPTIONS';
          setInventoryState(false);
          if (restockState) {
            restockState.hidden = true;
            restockState.setAttribute('aria-hidden', 'true');
          }
          if (soldOutOverlay) {
            soldOutOverlay.hidden = true;
            soldOutOverlay.setAttribute('aria-hidden', 'true');
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (price) price.innerHTML = variant.compareAtPrice && variant.compareAtPrice > variant.price
          ? `<s>${variant.compareAtFormatted}</s> ${variant.priceFormatted}`
          : variant.priceFormatted;
        if (mobilePrice) mobilePrice.innerHTML = variant.compareAtPrice && variant.compareAtPrice > variant.price
          ? `<s>${variant.compareAtFormatted}</s> ${variant.priceFormatted}`
          : variant.priceFormatted;
        if (addButton) {
          addButton.disabled = !variant.available || (requiredLineItem && !selectedLineItem);
          addButton.textContent = !variant.available ? 'SOLD OUT' : (requiredLineItem && !selectedLineItem ? 'CHOOSE A SIZE' : 'ADD TO BAG');
        }
        if (availability) availability.textContent = !variant.available ? 'SOLD OUT' : (requiredLineItem && !selectedLineItem ? 'SELECT A SIZE' : 'AVAILABLE');
        setInventoryState(variant.available);
        if (mobileAdd) {
          mobileAdd.disabled = !variant.available || (requiredLineItem && !selectedLineItem);
          mobileAdd.textContent = !variant.available ? 'SOLD OUT' : (requiredLineItem && !selectedLineItem ? 'CHOOSE A SIZE' : 'ADD TO BAG');
        }
        if (window.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      }

      form.addEventListener('change', update);
      mobileAdd?.addEventListener('click', () => form.requestSubmit());
      update();
    });
  }

  function initProductGalleryCounters(root = document) {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.counterReady === 'true') return;
      const frame = gallery.closest('.product-gallery-frame') || gallery;
      const counter = frame.querySelector('[data-gallery-count]');
      gallery.dataset.counterReady = 'true';
      const total = gallery.querySelectorAll('.gallery-image').length;
      const prev = frame.querySelector('[data-gallery-prev]');
      const next = frame.querySelector('[data-gallery-next]');
      const slides = Array.from(gallery.querySelectorAll('.gallery-image'));
      let activeIndex = 0;
      const getActiveIndex = () => slides.reduce((closest, slide, index) => {
        const closestDistance = Math.abs(slides[closest].offsetLeft - gallery.scrollLeft);
        const distance = Math.abs(slide.offsetLeft - gallery.scrollLeft);
        return distance < closestDistance ? index : closest;
      }, 0);
      const update = () => {
        if (!total) return;
        // Use each slide's actual offset instead of assuming its width is the
        // same as the scroll container. This keeps navigation reliable when
        // responsive gutters or fractional widths are present.
        activeIndex = getActiveIndex();
        if (counter) counter.textContent = `${activeIndex + 1} / ${total}`;
        if (prev) prev.disabled = activeIndex === 0;
        if (next) next.disabled = activeIndex === total - 1;
      };
      const move = (direction) => {
        // Re-read the current snap position on every click. During a smooth
        // scroll, a scroll event can briefly report the previous position;
        // relying only on the cached index would make the second click repeat
        // the first transition.
        activeIndex = getActiveIndex();
        const targetIndex = Math.min(total - 1, Math.max(0, activeIndex + direction));
        const target = slides[targetIndex];
        if (!target) return;
        activeIndex = targetIndex;
        gallery.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        if (counter) counter.textContent = `${activeIndex + 1} / ${total}`;
        if (prev) prev.disabled = activeIndex === 0;
        if (next) next.disabled = activeIndex === total - 1;
      };
      gallery.addEventListener('scroll', update, { passive: true });
      prev?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      update();
    });
  }

  function initRelatedProducts(root = document) {
    root.querySelectorAll('[data-related-products]').forEach(async (section) => {
      if (section.dataset.ready === 'true' || !section.dataset.url) return;
      section.dataset.ready = 'true';
      try {
        const response = await fetch(section.dataset.url, { headers: { Accept: 'text/html' } });
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsed.querySelector('[data-related-products]');
        if (replacement?.innerHTML.trim()) section.replaceWith(replacement);
      } catch (error) {
        console.warn('Related products could not be loaded.', error);
      }
    });
  }

  async function refreshCart(openAfter = false) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh bag');
    const sections = await response.json();
    const html = sections['cart-drawer'];
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('#shopify-section-cart-drawer');
    const current = document.querySelector('#shopify-section-cart-drawer');
    if (replacement && current) current.replaceWith(replacement);
    const drawer = document.getElementById('CartDrawer');
    const count = drawer?.dataset.cartCountValue || '0';
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
    if (openAfter) openDrawer('CartDrawer');
  }

  async function addProduct(form) {
    const button = form.querySelector('[data-add-to-cart]');
    const original = button?.textContent;
    if (button) { button.disabled = true; button.textContent = 'ADDING…'; }
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'This piece could not be added.');
      await refreshCart(true);
      announce(`${payload.product_title || 'Item'} added to your bag.`);
    } catch (error) {
      announce(error.message || 'This piece could not be added.');
      const feedback = form.querySelector('[data-product-feedback]');
      if (feedback) feedback.textContent = error.message || 'This piece could not be added.';
    } finally {
      if (button && document.body.contains(button)) { button.disabled = false; button.textContent = original; }
    }
  }

  async function changeCart(line, quantity) {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!response.ok) throw new Error('Bag update failed.');
      await refreshCart(true);
      announce('Your bag was updated.');
    } catch (error) { announce(error.message || 'Bag update failed.'); }
  }

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-drawer-open]');
    const close = event.target.closest('[data-drawer-close]');
    const swap = event.target.closest('[data-drawer-swap]');
    const size = event.target.closest('[data-size-guide-open]');
    const sizeTab = event.target.closest('[data-size-tab]');
    const cartChange = event.target.closest('[data-cart-change]');
    if (open) openDrawer(open.dataset.drawerOpen, open);
    if (close) closeDrawers();
    if (swap) openDrawer(swap.dataset.drawerSwap, swap);
    if (size) openDrawer('SizeGuide', size);
    if (sizeTab) {
      const guide = sizeTab.closest('.size-guide-content');
      guide?.querySelectorAll('[data-size-tab]').forEach((tab) => tab.setAttribute('aria-selected', String(tab === sizeTab)));
      guide?.querySelectorAll('[data-size-panel]').forEach((panel) => { panel.hidden = panel.dataset.sizePanel !== sizeTab.dataset.sizeTab; });
    }
    if (event.target.matches('[data-site-overlay]')) closeDrawers();
    if (cartChange) changeCart(Number(cartChange.dataset.line), Number(cartChange.dataset.quantity));
  });

  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawers(); });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    addProduct(form);
  });
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-by]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    window.location.assign(url.toString());
  });
  document.addEventListener('shopify:section:load', (event) => {
    initCarousels(event.target);
    initProductForms(event.target);
    initProductGalleryCounters(event.target);
    initRelatedProducts(event.target);
  });

  initCarousels();
  initProductForms();
  initProductGalleryCounters();
  initRelatedProducts();
})();
